"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";
import ServiceQuickNav from "@/components/ServiceQuickNav";

const azureServices = [
  { icon: "🖥️", title: "Azure Infrastructure", desc: "Virtual machines, virtual networks, load balancers, and availability zones for enterprise-grade cloud infrastructure." },
  { icon: "🖥️", title: "Azure Virtual Desktop", desc: "Cloud-hosted desktops for remote and hybrid teams — secure, scalable, and accessible from any device." },
  { icon: "💾", title: "Azure Backup & Site Recovery", desc: "Business continuity and disaster recovery in the cloud — automated backups and rapid failover capabilities." },
  { icon: "🔒", title: "Azure Security Centre", desc: "Threat protection, compliance monitoring, and security policies across your entire Azure environment." },
  { icon: "🔑", title: "Azure Active Directory", desc: "Identity management, single sign-on, conditional access, and multi-factor authentication for your organisation." },
  { icon: "🔗", title: "Azure Hybrid Cloud", desc: "Seamlessly connect your on-premise infrastructure to Azure for a true hybrid cloud environment." },
  { icon: "🗄️", title: "Azure Storage Solutions", desc: "Blob storage, file shares, archive and backup storage — scalable and cost-effective cloud storage." },
  { icon: "📊", title: "Azure Monitoring & Management", desc: "Cost management, resource monitoring, and continuous optimisation of your Azure environment." },
];

const migrationSteps = [
  { num: "01", title: "Assessment", desc: "Evaluate your current infrastructure, workloads, and Azure readiness." },
  { num: "02", title: "Design", desc: "Architect your Azure environment with security and scalability built in." },
  { num: "03", title: "Migration", desc: "Structured migration of workloads to Azure with minimal disruption." },
  { num: "04", title: "Testing", desc: "Validate all migrated services and verify security configurations." },
  { num: "05", title: "Optimisation", desc: "Fine-tune performance, cost, and security post-migration." },
  { num: "06", title: "Management", desc: "Ongoing monitoring, management, and support for your Azure environment." },
];

const benefits = [
  { icon: "📈", title: "Scalable Infrastructure", value: "Elastic", desc: "Scale resources up or down instantly to match your business demands." },
  { icon: "🔒", title: "Enterprise Security", value: "Built-in", desc: "Microsoft’s enterprise-grade security protecting your cloud environment." },
  { icon: "⏱️", title: "Uptime SLA", value: "99.9%", desc: "Microsoft Azure’s guaranteed uptime SLA for production workloads." },
  { icon: "💰", title: "Cost Optimisation", value: "Pay-as-you-go", desc: "Only pay for what you use with Azure’s flexible pricing models." },
  { icon: "🌍", title: "Global Availability", value: "60+ Regions", desc: "Deploy workloads in Microsoft’s global network of data centres." },
];

export default function AzurePage() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Cloud Solutions", href: "/cloud-solutions" },
            { label: "Microsoft Azure" },
          ]}
        />

        {/* Hero */}
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 40%, #001a3a 70%, #070B14 100%)" }} />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,137,214,0.15) 0%, transparent 70%)" }} />
          <div className="absolute bottom-1/4 left-1/3 w-80 h-80 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,89,214,0.08) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(0,137,214,0.12)", border: "1px solid rgba(0,137,214,0.3)" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: "#0089D6" }} />
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Microsoft Azure</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                Microsoft Azure Cloud Solutions<br />
                <span style={{ background: "linear-gradient(135deg, #0089D6, #38BDF8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>for New Zealand Businesses</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg max-w-2xl mb-10" style={{ color: "#94A3B8" }}>
                Pro IT Solutions designs, deploys, and manages Microsoft Azure environments — from cloud infrastructure and virtual machines to security, backup, and hybrid cloud solutions.
              </p>
            </FadeInView>
            <FadeInView delay={0.45}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-600 rounded-pill" style={{ background: "linear-gradient(135deg, #0089D6, #0EA5E9)", color: "white" }}>Book an Azure Assessment</Link>
                <Link href="#azure-services" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-500 rounded-pill btn-secondary">View Azure Services</Link>
              </div>
            </FadeInView>
            <FadeInView delay={0.6}>
              <div className="flex flex-wrap justify-center gap-6 mt-12">
                {["Microsoft Azure", "Azure Certified", "Microsoft Partner"]?.map((t) => (
                  <div key={t} className="flex items-center gap-2 text-[13px] font-body" style={{ color: "#64748B" }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#0089D6" }} />
                    {t}
                  </div>
                ))}
              </div>
            </FadeInView>
          </div>
        </section>

        {/* Azure Services */}
        <section id="azure-services" className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Azure Services</h2>
              <p className="text-body-lg max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>Comprehensive Microsoft Azure solutions designed and managed by certified Azure specialists.</p>
            </FadeInView>
            {/* Desktop grid */}
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch" staggerDelay={0.08}>
              {azureServices?.map((svc) => (
                <div key={svc?.title} className="rounded-card-lg p-6 transition-all duration-300 hover:border-blue-500/30 h-full flex flex-col" style={{ background: "rgba(0,137,214,0.05)", border: "1px solid rgba(0,137,214,0.15)" }}>
                  <div className="text-3xl mb-4">{svc?.icon}</div>
                  <h3 className="font-heading font-700 text-[24px] mb-2" style={{ color: "#F8FAFC" }}>{svc?.title}</h3>
                  <p className="text-[14px] font-body leading-[1.75]" style={{ color: "#94A3B8" }}>{svc?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Migration Process */}
        <section className="py-20 lg:py-28 border-t" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Azure Migration Process</h2>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 items-stretch" staggerDelay={0.08}>
              {migrationSteps?.map((step) => (
                <div key={step?.num} className="rounded-card-lg p-5 text-center h-full flex flex-col" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="font-heading font-800 text-[32px] mb-2" style={{ color: "rgba(0,137,214,0.4)" }}>{step?.num}</div>
                  <h3 className="font-heading font-600 text-[14px] mb-1" style={{ color: "#F8FAFC" }}>{step?.title}</h3>
                  <p className="text-[12px] font-body" style={{ color: "#94A3B8" }}>{step?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Why Microsoft Azure?</h2>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 items-stretch" staggerDelay={0.08}>
              {benefits?.map((b) => (
                <div key={b?.title} className="rounded-card-lg p-6 text-center h-full flex flex-col" style={{ background: "rgba(0,137,214,0.05)", border: "1px solid rgba(0,137,214,0.15)" }}>
                  <div className="text-2xl mb-3">{b?.icon}</div>
                  <div className="font-heading font-700 text-[18px] mb-1" style={{ color: "#0EA5E9" }}>{b?.value}</div>
                  <div className="font-heading font-600 text-[13px] mb-2" style={{ color: "#F8FAFC" }}>{b?.title}</div>
                  <p className="text-[12px] font-body" style={{ color: "#94A3B8" }}>{b?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Microsoft Partner Badge */}
        <section className="py-12 border-t border-b" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView>
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl" style={{ background: "rgba(0,137,214,0.15)", border: "1px solid rgba(0,137,214,0.3)" }}>🔷</div>
                <div>
                  <div className="font-heading font-700 text-[20px] mb-1" style={{ color: "#F8FAFC" }}>Microsoft Partner — Azure Certified</div>
                  <p className="text-[14px] font-body" style={{ color: "#94A3B8" }}>Pro IT Solutions is a certified Microsoft Partner with Azure expertise across infrastructure, security, and hybrid cloud.</p>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(0,137,214,0.08)", border: "1px solid rgba(0,137,214,0.25)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Ready to move to Azure?</h2>
                <p className="text-body-lg mb-8" style={{ color: "#94A3B8" }}>Book a free Azure assessment and let our certified Azure specialists design the right cloud environment for your business.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600" style={{ background: "linear-gradient(135deg, #0089D6, #0EA5E9)", color: "white" }}>Book a Free Azure Assessment</Link>
                  <Link href="/cloud-solutions" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-500 btn-secondary">View All Cloud Services</Link>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        <ServiceQuickNav currentPath="/cloud-solutions/azure" />

        <Footer />
      </main>
    </PageTransition>
  );
}
